import cloneDeep from 'lodash/cloneDeep';
import floor from 'lodash/floor';
import rgbHex from 'rgb-hex';
import tinycolor from 'tinycolor2';
import { Layer as ParseLayer, RGB, RGBA } from '@meitu-graphic/ag-psd';
import { FontServer } from '@meitu/saas-editor-core/es/framework/core/extensions/font-server';
import { translateAngleToXY } from '@meitu/saas-editor-core/es/utils/math-kit';
import { decomposeLayerTextTransform } from '@meitu/saas-editor-core/es/utils/math-kit/decomposeLayerTextTransform';
import { fixTextSize, oneLineAddHalfSize } from '@meitu/saas-editor-core/es/utils/text-kit/base';
import { TextMeasurer } from '@meitu/saas-editor-core/es/utils/text-kit/measure';
import { DefaultClientTextLayer, getDefaultTextGradient } from '@meitu/saas-editor-core/es/version-parse/constants';
import {
    DEFAULT_FONT_FAMILY,
    IFontGlow,
    IFontShadow,
    IFontStroke,
    ILayerProps,
    ITextContent,
    ITextLayerProps,
    LayerType,
    StrokeDirectionType,
    TextAlignType,
    TextWritingType,
} from '@meitu/saas-editor-core/es/version-types';
import { adjustTextPositionByFont, getFontInfoByUrl } from './font';
import { IFont, PsdParseWarnType, WarnOrErrorCallbackFunc } from './types';
import { getGradientColor, isGradientColor } from './utils';

const colorMax255 = (color: number) => {
    if (color > 255) {
        color = 255;
    }

    return color;
};

export function getLineSpacing(textData: any, fontSize?: number) {
    // ps 不同版本 leading 计算方式不一样
    // 2024-12-24 修改leadiing获取规则，只有autoLeading显性设置为false，才读取leading

    if (
        !textData.style ||
        typeof textData.style.autoLeading === 'undefined' ||
        textData.style.autoLeading ||
        !textData.style.leading
    ) {
        return 1.2;
    }
    return textData.style.leading / (fontSize || textData.style.fontSize || 12);
}

const getOpacityOrDefault = (value = 1) => {
    return value;
};

/**
 * @description rgba 转 十六进制颜色值
 * @param  rgbaArr rgba 颜色值
 * @param  isAddAlpha alpha参数
 * @return 返回十六进制的颜色值 对应 rgb 和 rgba
 */
export function rgbArr2Hex(rgbaArr: any, isAddAlpha = false) {
    let strHex = '#';
    const colorArr = rgbaArr.slice(0, 3);
    // 转成16进制
    colorArr.forEach((v: any) => {
        let hex = (+v.toFixed(0)).toString(16);
        // 补位0
        hex = hex.padStart(2, '0');
        strHex += hex;
    });
    return strHex + (isAddAlpha ? 'ff' : '');
}

/** 解析阴影 */
export const parseTextShadow = (layer: ParseLayer) => {
    const textShadow = [] as IFontShadow[];

    if (layer.effects && layer.effects.dropShadow && layer.effects.dropShadow) {
        layer.effects.dropShadow.forEach((data) => {
            if (!data.enabled) {
                return;
            }

            const rgb = (data.color as RGB) || { r: 255, g: 255, b: 255 };
            const color = `#${rgbHex(
                colorMax255(rgb.r),
                colorMax255(rgb.g),
                colorMax255(rgb.b),
                getOpacityOrDefault((data.opacity || 1) * (layer.fillOpacity || 1)),
            )}`;

            const angle = (data.angle || 0) + 180;
            const distance = data.distance?.value || 0;
            const size = data.size?.value || 0;
            textShadow.push({
                color,
                x: translateAngleToXY(angle, distance).x,
                y: translateAngleToXY(angle, distance).y,
                // 半径
                blur: size,
                hidden: false,
                // 是否可编辑
                editable: true,
            });
        });
    }

    return textShadow;
};

/** 解析发光 */
export const parseTextGlow = (layer: ParseLayer) => {
    const textGlow = [] as IFontGlow[];

    if (layer.effects && layer.effects.outerGlow && layer.effects.outerGlow.enabled) {
        const data = layer.effects.outerGlow;

        const rgb = (data.color as RGB) || { r: 255, g: 255, b: 255 };
        const color = `#${rgbHex(
            colorMax255(rgb.r),
            colorMax255(rgb.g),
            colorMax255(rgb.b),
            getOpacityOrDefault(data.opacity),
        )}`;

        textGlow.push({
            color,
            blur: 0,
            width: data.size?.value || 0,
            hidden: false,
            editable: true,
        });
    }
    return textGlow;
};

/** 解析描边 */
export const parseTextStroke = (layer: ParseLayer) => {
    const textStroke = [] as IFontStroke[];

    if (layer.effects && layer.effects.stroke) {
        layer.effects.stroke.forEach((data) => {
            if (!data.enabled) {
                return;
            }

            const rgb = (data.color as RGB) || { r: 255, g: 255, b: 255 };
            const color = `#${rgbHex(
                colorMax255(rgb.r),
                colorMax255(rgb.g),
                colorMax255(rgb.b),
                getOpacityOrDefault(data.opacity),
            )}`;
            textStroke.push({
                color,
                width: data.size?.value || 0,
                direction: StrokeDirectionType.WITHOUT,
                hidden: false,
                editable: true,
            });
        });
    }
    return textStroke.length > 0 ? [textStroke[0]] : textStroke;
};

export const getTextLayer = async (
    stageWidth: number,
    stageHeight: number,
    parentLayer: ParseLayer,
    layer: ParseLayer,
    fontList: any[],
    noFixFontLayer: string[],
    warnOrErrorCallback?: WarnOrErrorCallbackFunc,
    noReplaceFontFamily = false,
    parseNoLimit = false,
    parseLayerCallback?: (originLayer: ParseLayer, resultLayer: ILayerProps) => Promise<ILayerProps | null>,
) => {
    const textLayer = cloneDeep(DefaultClientTextLayer);
    textLayer.opacity = layer.opacity || 1 * (layer.fillOpacity || 1);
    textLayer.id = layer.id?.toString() || textLayer.id;
    textLayer.opacity = layer.opacity || 1 * (layer.fillOpacity || 1);
    textLayer.title = layer.name || '';
    textLayer.parentId = parentLayer.id && parentLayer.id === layer.id ? '' : parentLayer.id?.toString();

    warnOrErrorCallback && warnOrErrorCallback(`解析文本图层：${textLayer.title}`, PsdParseWarnType.Info);
    // 文字数据
    const { text: textData, canvas } = layer;

    if (textData === undefined) {
        return textLayer;
    }

    const { transform, style, styleRuns } = textData;

    let textScale = 1;
    let fontSize = 24;
    let textColor: RGBA = { r: 0, g: 0, b: 0, a: 0 };
    let letterSpacing = 0;
    const isBox = layer.text?.shapeType === 'box' && layer.text?.boxBounds;
    if (isBox) {
        const [startX, startY, endX, endY] = layer.text?.boxBounds || [];
        textLayer.width = endX - startX;
        textLayer.height = endY - startY;
        textLayer.top = startY;
        noFixFontLayer.push(textLayer.id);
    } else if (canvas) {
        textLayer.width = canvas.width;
        textLayer.height = canvas.height;
    }

    if (transform) {
        const transformInfo = decomposeLayerTextTransform(transform);
        const { xScale, yScale, radian } = transformInfo[0];
        const { x, y } = transformInfo[4];
        // 包围盒时，宽高不乘transform.textScale
        textScale = Math.abs(Math.min(xScale, yScale));
        textLayer.rotate = radian;
        textLayer.top = layer.top || 0 - y;
        textLayer.left = layer.left || 0 - x;
        textLayer.width = (textLayer.width || 0) * (isBox ? textScale : 1);
        textLayer.height = (textLayer.height || 0) * (isBox ? textScale : 1);
    }
    let lineSpacing = getLineSpacing(textData);
    console.log('lineSpacing', lineSpacing);
    if (style && style.fontSize) {
        // if (!style.fontSize) {
        //     warnOrErrorCallback &&
        //         warnOrErrorCallback(
        //             `文本图层${textLayer.title}：字体大小为空`,
        //             PsdParseWarnType.Error,
        //         );
        // }
        fontSize = (style.fontSize || 12) * textScale;
        if (layer.text?.bounds && layer.text?.boundingBox) {
            // (textLayer as any).subHeight =
            //     (layer.text?.boundingBox.top.value -
            //         layer.text?.bounds.top.value) *
            //         textScale || 0;
            // (textLayer as any).subTop =
            //     (layer.text?.bounds.top.value * textScale || 0) - textLayer.top;
            // (textLayer as any).originHeight =
            //     (layer.text?.bounds.bottom.value -
            //         layer.text?.bounds.top.value) *
            //         textScale || 0;
            (textLayer as any).bounds = {
                bounds: layer.text?.bounds,
                boundingBox: layer.text?.boundingBox,
                textScale: textScale || 1,
                originInfo: {
                    width: layer.canvas?.width,
                    height: layer.canvas?.height,
                    top: layer.top,
                    left: layer.left,
                },
                isBox,
            };
        }
        /**
         * 保留两位小数修复字间距数值不精准bug
         */
        letterSpacing = floor(((style.tracking || 0) / 1000) * fontSize, 2);
    } else if (styleRuns && styleRuns.length > 0 && styleRuns[0].style && Object.keys(styleRuns[0].style).length > 0) {
        const { length, style } = styleRuns[0];
        fontSize = Math.round(style.fontSize ? (style.fontSize || 12) * textScale : fontSize);
        letterSpacing = floor(((style.tracking || 0) / 1000) * fontSize, 2);
        lineSpacing = getLineSpacing(textData, style.fontSize || 12);
    } else {
        fontSize = 12 * textScale;

        /**
         * 保留两位小数修复字间距数值不精准bug
         */
        letterSpacing = floor((0 / 1000) * fontSize, 2);
    }

    let textGradient = getDefaultTextGradient();
    if (isGradientColor(layer)) {
        const gradientColor = getGradientColor(layer);
        const { colors, type, angle } = gradientColor;
        textGradient = {
            angle: -(angle - 90) % 360,
            colors,
        };
    } else if (style) {
        textColor = (style.fillColor as RGBA) || textColor;
    }

    // 文字方向
    const writingMode =
        textData.orientation === 'vertical' ? TextWritingType.VERTICAL_RL : TextWritingType.HORIZONTAL_TB;

    let textAlign: TextAlignType = TextAlignType.LEFT;
    if (textData.paragraphStyle && textData.paragraphStyle.justification) {
        textAlign = textData.paragraphStyle.justification.includes('justify')
            ? TextAlignType.JUSTIFY
            : (textData.paragraphStyle.justification as TextAlignType);
    } else if (textData.paragraphStyleRuns && textData.paragraphStyleRuns[0]?.style.justification) {
        textAlign = textData.paragraphStyleRuns[0]?.style.justification.includes('justify')
            ? TextAlignType.JUSTIFY
            : (textData.paragraphStyleRuns[0]?.style.justification as TextAlignType);
    }

    let text = textData.text.replace(/(\r\n|\r)/g, '\n');

    if (text && style && style.fontCaps && style.fontCaps === 2) {
        text = text.toUpperCase();
    }

    const textShadow = parseTextShadow(layer);
    const textGlow = parseTextGlow(layer);
    const textStroke = parseTextStroke(layer);

    const rgb = textColor || { r: 255, g: 255, b: 255 };
    const color = `#${rgbHex(
        colorMax255(rgb.r),
        colorMax255(rgb.g),
        colorMax255(rgb.b),
        getOpacityOrDefault((layer.fillOpacity || 1) * (layer.opacity || 1)),
    )}`;

    textLayer.data = {
        text,
        bold: textData.style?.fauxBold || false,
        color,
        fontFamily: (textData.style?.font && textData.style.font.name) || 'AlibabaPuHuiTi-Regular',
        fontSize,
        italic: textData.style?.fauxItalic || false,
        letterSpacing,
        lineSpacing,
        lineThrough: textData.style?.strikethrough || false,
        underline: textData.style?.underline || false,
        textAlign,
        writingMode,
        textGlow,
        textShadow,
        textStroke,
        textBackground: [],
        listStyle: '',
        textGradient,
    };

    const textContents: ITextContent[] = [];
    if (styleRuns && styleRuns.length > 0 && styleRuns[0].style && Object.keys(styleRuns[0].style).length > 0) {
        let textIndex = 0;
        for (let i = 0; i < styleRuns.length; i++) {
            const { length, style } = styleRuns[i];
            const { text, color, fontSize, fontFamily, bold, italic, lineThrough, underline } = textLayer.data;
            textContents.push({
                text: text.substring(textIndex, textIndex + length),
                color: style.fillColor ? `#${tinycolor(style.fillColor as RGBA).toHex()}` : color,
                fontSize: Math.round(style.fontSize ? (style.fontSize || 12) * textScale : fontSize),
                fontFamily: style.font ? style?.font && style.font.name : fontFamily,
                bold: style?.fauxBold !== undefined ? style.fauxBold : bold,
                italic: style?.fauxItalic !== undefined ? style.fauxItalic : italic,
                lineThrough: style?.strikethrough !== undefined ? style.strikethrough : lineThrough,
                underline: style?.underline !== undefined ? style.underline : underline,
            });

            textIndex = textIndex + length;
        }
    }

    if (textContents.length > 0) {
        textLayer.data.textContents = textContents;
    }

    textLayer.shine = true;

    if (stageWidth < fontSize) {
        warnOrErrorCallback &&
            !parseNoLimit &&
            warnOrErrorCallback(`文本图层${textLayer.title}：字体大小不能大于模板的宽度`, PsdParseWarnType.Error);
    }

    if (textLayer.width <= 0 || textLayer.height <= 0) {
        if (parseNoLimit) {
            return null;
        }
        warnOrErrorCallback && warnOrErrorCallback(`文本图层${textLayer.title}：宽高不能小于0`, PsdParseWarnType.Error);
    }

    if (textLayer.width < 6 && textLayer.height < 6) {
        warnOrErrorCallback &&
            !parseNoLimit &&
            warnOrErrorCallback(`文本图层${textLayer.title}：宽高不能都小于6px`, PsdParseWarnType.Error);
    }

    if (lineSpacing < 0) {
        warnOrErrorCallback &&
            warnOrErrorCallback(`文本图层${textLayer.title}：行间距小于0`, PsdParseWarnType.Warn, textLayer.id);
    }

    if (lineSpacing < 1) {
        warnOrErrorCallback &&
            warnOrErrorCallback(`文本图层${textLayer.title}：行间距小于字体大小`, PsdParseWarnType.Warn, textLayer.id);
    }

    if (fontSize < 12) {
        warnOrErrorCallback &&
            warnOrErrorCallback(`文本图层${textLayer.title}：字体大小小于12`, PsdParseWarnType.Warn, textLayer.id);
    }

    if (!text || !text.replace(/\s*/g, '')) {
        warnOrErrorCallback &&
            !parseNoLimit &&
            warnOrErrorCallback(
                `文本图层${textLayer.title}：文本为空，可能是个空图层`,
                PsdParseWarnType.Warn,
                textLayer.id,
            );
    }

    if (parseLayerCallback) {
        const newLayer = await parseLayerCallback(layer, textLayer);
        return newLayer;
    }

    return textLayer;
};

const fontServer = new FontServer('');

export const fixFont = async (
    layers: ILayerProps[],
    fonts: IFont[],
    defaultFont: string,
    noFixFontLayer: string[],
    noReplaceFontFamily = false,
) => {
    const fix = async (fontFamily: string, fonts: IFont[], defaultFont: string, layer: ILayerProps) => {
        const font = fonts.find((font) => fontFamily === font.fontName);

        if (!font && noReplaceFontFamily) {
            return null;
        }
        const data = layer.data as ITextLayerProps;
        const { textContents } = layer.data as ITextLayerProps;

        if (textContents && textContents.length > 0) {
            for (let i = 0; i < textContents.length; i++) {
                const content = textContents[i];
                const subFontFamily = content.fontFamily || (layer.data as ITextLayerProps).fontFamily;
                const subFont = fonts.find((font) => subFontFamily === font.fontName);
                content.fontFamily = subFont ? subFont.fontName : defaultFont;
                await fontServer.loadFont(
                    content.fontFamily,
                    subFont ? subFont.fontUrl : DEFAULT_FONT_FAMILY().ttf_url,
                );
            }
        }
        data.fontFamily = font ? font.fontName : defaultFont;

        await fontServer.loadFont(data.fontFamily, font ? font.fontUrl : DEFAULT_FONT_FAMILY().ttf_url);

        let result = {
            width: layer.width,
            height: layer.height,
        };
        const lines = (layer.data as ITextLayerProps).text.split('\n').length;
        const { writingMode } = data;

        const hasRotate = !!layer.rotate;
        if (!noFixFontLayer.includes(layer.id)) {
            if (lines === 1) {
                (layer.data as ITextLayerProps).lineSpacing = 1;
            }
            result = await fixTextSize(layer, lines > 1);

            // result = await fixWidth({
            //     ...layer,
            //     ...result
            // });

            if (!hasRotate) {
                result = await oneLineAddHalfSize({
                    ...layer,
                    ...result,
                });
            }
        }

        if (hasRotate && lines !== 1) {
            layer.left = layer.left - (result.width - layer.width) / 2;
            layer.top = layer.top - (result.height - layer.height) / 2;
        } else {
            if (writingMode === TextWritingType.HORIZONTAL_TB) {
                if (result.width !== layer.width && lines === 1) {
                    if ((layer.data as ITextLayerProps).textAlign === TextAlignType.CENTER) {
                        layer.left = layer.left - (result.width - layer.width) / 2;
                    } else if ((layer.data as ITextLayerProps).textAlign === TextAlignType.RIGHT) {
                        layer.left = layer.left + layer.width - result.width;
                    }

                    if (lines === 1) {
                        (layer.data as ITextLayerProps).lineSpacing = 1;
                    }

                    if ((layer as any).bounds) {
                        let fontInfo = null as any;
                        fontInfo = await getFontInfoByUrl(font?.fontUrl || '');
                        const topOffset = await adjustTextPositionByFont(
                            fontInfo,
                            (layer as any).bounds,
                            result.height,
                            layer.top,
                            (layer.data as ITextLayerProps).fontSize,
                            (layer.data as ITextLayerProps).lineSpacing,
                        );

                        if (
                            (layer.data as ITextLayerProps).lineSpacing * (layer.data as ITextLayerProps).fontSize >=
                            Math.abs(topOffset)
                        ) {
                            layer.top += topOffset;

                            if (hasRotate && (layer as any).bounds && (layer as any).bounds.originInfo) {
                                const { top, left, width, height } = (layer as any).bounds.originInfo;
                                layer.left = left + width / 2 - result.width / 2;
                                layer.top = top + height / 2 - result.height / 2;
                            }

                            if (
                                (layer.data as ITextLayerProps).textAlign === TextAlignType.LEFT &&
                                (layer as any).bounds.boundingBox.left < (layer.data as ITextLayerProps).fontSize
                            ) {
                                layer.left =
                                    layer.left -
                                    ((layer as any).bounds.boundingBox.left.value || 0) *
                                        (layer as any).bounds.textScale;
                            }
                        }
                    }
                } else {
                    if ((layer as any).bounds && (layer as any).bounds.isBox) {
                        //FIXME  字形偏高或者偏低会有误差  如果字形在中间的就是准的
                        const info = await TextMeasurer.measure(layer, {
                            keepConstantHeight: true,
                        });
                        layer.top =
                            layer.top - (info.height - (layer as any).bounds?.originInfo?.height || layer.height) / 2;
                    } else {
                        layer.top = layer.top - (result.height - layer.height) / 2;
                    }
                }
            } else {
                layer.left = layer.left - (result.width - layer.width) / 2;
            }
        }

        delete (layer as any).bounds;
        layer.width = result.width;
        if (lines > 1) {
            layer.width =
                layer.width +
                (layer.data as ITextLayerProps).fontSize / 2 +
                (layer.data as ITextLayerProps).letterSpacing;
        }
        layer.height = result.height;
        return !font ? fontFamily : null;
    };

    const replaceFonts = [] as any[];
    for (let i = 0; i < layers.length; i++) {
        const layer = layers[i] as ILayerProps;
        if (layer.layerType === LayerType.TEXT_LAYER) {
            const data = layer.data as ITextLayerProps;

            const replaceFont = await fix(data.fontFamily, fonts, defaultFont, layer);
            if (replaceFont) {
                replaceFonts.push({ layer, replaceFont });
            }
        }
    }

    return replaceFonts;
};
